import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  AccurateTimeDetails,
  AssignmentStack,
  Label,
  OperationStatus,
  Project,
  RoleAction,
  RolePermissionSubject,
  ShiftColorOptions,
} from '@wilson/interfaces';

import { AbilityServiceSignal } from '@casl/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthorizationModule } from '@wilson/authorization';
import { TimelineShift } from '@wilson/interfaces';
import { ShiftTimelinePipesModule } from '@wilson/shift-timeline/pipes';
import {
  NzDropdownMenuComponent,
  NzDropdownModule,
} from 'ng-zorro-antd/dropdown';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { DeferredAssignmentShiftCardCellViewHelperService } from '../../deferred-assignment-shift-card-cell-view/deferred-assignment-shift-card-cell-view-helper.service';
import { IsShiftSelectedPipe } from '../../pipes/is-shift-selected.pipe';
import { ShiftCardColorStylePipe } from '../../pipes/shift-card-color-style.pipe';
import { ShiftPresenceBorderColorPipe } from '../../pipes/shift-presence-border-color.pipe';
import { GroupItemsSelectionHelperService } from '../../services/group-items-selection-helper.service';
import { ShiftMultiSelectHelperService } from '../../services/shift-multi-select-helper.service';
import { ShiftCardTooltipStateService } from '../../shift-cards/tooltips/shift-card-tooltip/shift-card-tooltip-state.service';
import { ShiftValidationTooltipWrapperComponent } from '../../shift-cards/tooltips/shift-validation-tooltip-wrapper/shift-validation-tooltip-wrapper.component';

@Component({
  selector: 'wilson-cell-view-shift-card',
  imports: [
    CommonModule,
    ShiftValidationTooltipWrapperComponent,
    IsShiftSelectedPipe,
    ShiftPresenceBorderColorPipe,
    ShiftCardColorStylePipe,
    NzDropdownModule,
    FeatureFlagsModule,
    ShiftTimelinePipesModule,
    AuthorizationModule,
    NzTooltipModule,
    TranslatePipe,
  ],
  templateUrl: './cell-view-shift-card.component.html',
  styleUrl: './cell-view-shift-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CellViewShiftCardComponent implements OnChanges {
  protected readonly shiftCardTooltipStateService = inject(
    ShiftCardTooltipStateService,
  );
  protected readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  protected readonly store = inject(Store);
  protected readonly deferredAssignmentShiftCardCellViewHelperService = inject(
    DeferredAssignmentShiftCardCellViewHelperService,
  );
  protected readonly groupItemsSelectionHelperService = inject(
    GroupItemsSelectionHelperService,
  );

  @Input({ required: true }) stackHighlightIds!: string[];
  @Input({ required: true }) userId!: string;
  @Input({ required: true }) isMoreThanOneAssignment!: boolean;
  @Input({ required: true }) isTooltipVisible!: boolean;
  @Input({ required: true }) assignmentStack!: AssignmentStack;
  @Input({ required: true }) isMinimalMode!: boolean;
  @Input({ required: true }) viewport!: HTMLElement | null;
  @Input({ required: true }) contextMenuTemplate!: NzDropdownMenuComponent;
  @Input({ required: true }) draggingItemId!: string | null;
  @Input({ required: true }) menuVisible!: boolean;
  @Input({ required: true }) isShiftDraggable!: boolean;
  @Input({ required: true }) isSelected!: boolean;
  @Input({ required: true }) isSelectionEnabled!: boolean;
  @Input({ required: true }) isAssignmentMode!: boolean;
  @Input() shiftColorOption!: ShiftColorOptions;
  @Input({ required: true }) isShiftModified!: boolean;
  @Input({ required: true }) areShiftsValid!: boolean;
  @Input({ required: true }) shiftStartDetail!: AccurateTimeDetails;
  @Input({ required: true }) shiftEndDetail!: AccurateTimeDetails;
  @Input({ required: true }) project!: Project | null | undefined;
  @Input({ required: true }) label!: Label | null | undefined;

  @Output() changeMenuVisibility = new EventEmitter<boolean>();
  @Output() shiftSelected = new EventEmitter<{
    event: MouseEvent;
    shiftId: string;
  }>();
  @Output() dragStarted = new EventEmitter<{
    shift: TimelineShift;
    isAssignmentMode: boolean;
    hasShiftUpdatePermissions: boolean;
  }>();
  @Output() dragEnded = new EventEmitter();
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;

  protected OperationStatus = OperationStatus;
  protected shift!: AssignmentStack['shifts'][0];
  protected can = inject(AbilityServiceSignal).can;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assignmentStack']) {
      if (this.assignmentStack.shifts.length) {
        this.shift = this.assignmentStack.shifts[0];
      }
    }
  }
}
